<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-users"></i>
          <span v-if="selectedOrganizationId != null">
            {{ noDataExist ? "Organization" : originalName }}
          </span>
          <span v-else-if="!loading">
            {{ $label("organization.new_organization", "New organization") }}
          </span>

          <organization-actions
            :organization-id="selectedOrganizationId"
          ></organization-actions>
        </h1>
      </div>
    </div>

    <div v-show="loading" class="row">
      <div class="col-xs-12">
        <view-loader
          margin-top-pixels="15"
          border-color="primary"
        ></view-loader>
      </div>
    </div>

    <div class="row"><div class="col-xs-12">&nbsp;</div></div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div class="row">
        <div class="col-sm-6">
          <h2>{{ $label("organization.organization", "Organization") }}</h2>
          <p></p>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Organization name -->
                  <section class="col col-6">
                    <text-input
                      v-model="selectedOrganization.name"
                      id="organization-name"
                      :label="$label('organization.name', 'Name')"
                      :placeholder="$label('organization.name', 'Name')"
                      :has-error="hasError('name')"
                      :error="getError('name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Organization name -->

                  <!-- BEGIN Organization type -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("organization.type", "Type")
                    }}</label>
                    <label class="select">
                      <select
                        id="organization-type"
                        v-model="selectedOrganization.type"
                        :disabled="updating"
                      >
                        <option
                          v-for="organizationType in organizationTypes"
                          v-bind:key="organizationType"
                          :value="organizationType"
                          :selected="
                            organizationTypeShouldBeSelected(organizationType)
                          "
                          >{{ organizationType }}</option
                        >
                      </select>
                      <i></i>
                    </label>
                  </section>
                  <!-- END Organization type -->
                </div>

                <div class="row">
                  <!-- BEGIN UID -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.uid"
                      id="organization-uid"
                      :label="$label('organization.uid', 'UID')"
                      :placeholder="$label('organization.uid', 'UID')"
                      :has-error="hasError('uid')"
                      :error="getError('uid')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END UID -->

                  <!-- BEGIN DVR -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.dvr"
                      id="organization-dvr"
                      :label="$label('organization.dvr', 'DVR')"
                      :placeholder="$label('organization.dvr', 'DVR')"
                      :has-error="hasError('dvr')"
                      :error="getError('dvr')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END DVR -->

                  <!-- BEGIN ZVR -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.zvr"
                      id="organization-zvr"
                      :label="$label('organization.zvr', 'ZVR')"
                      :placeholder="$label('organization.zvr', 'ZVR')"
                      :has-error="hasError('zvr')"
                      :error="getError('zvr')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                    <div class="selectVenues">
                      <a class="note action-link cursor-pointer" id="to-zvr">{{
                        $label("organization.go_to_zvr", "Go to ZVR")
                      }}</a>
                    </div>
                  </section>
                  <!-- END ZVR -->
                </div>

                <div class="row">
                  <!-- BEGIN Email -->
                  <section class="col col-sm-12 col-md-8">
                    <text-input
                      v-model="selectedOrganization.email"
                      id="organization-email"
                      :label="$label('organization.email', 'Email')"
                      :placeholder="$label('organization.email', 'Email')"
                      :has-error="hasError('email')"
                      :error="getError('email')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Email -->

                  <!-- BEGIN Founding year -->
                  <section class="col col-sm-12 col-md-4">
                    <text-input
                      v-model="selectedOrganization.founding_year"
                      id="organization-founding-year"
                      :label="
                        $label('organization.founding_year', 'Founding year')
                      "
                      :placeholder="
                        $label('organization.founding_year', 'Founding year')
                      "
                      :has-error="hasError('founding_year')"
                      :error="getError('founding_year')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Founding year -->
                </div>

                <!-- BEGIN Internal note -->
                <section>
                  <label class="label">{{
                    $label("organization.internal_note", "Internal note")
                  }}</label>
                  <label class="textarea">
                    <i class="icon-append icon-info-sign"></i>
                    <textarea
                      v-model="selectedOrganization.internal_note"
                      :class="{ 'text-input-error': hasError('internal_note') }"
                      :placeholder="
                        $label('organization.internal_note', 'Internal note')
                      "
                      :disabled="updating"
                      id="organization-internal-note"
                      rows="6"
                    >
                    </textarea>
                  </label>
                </section>
                <!-- END Internal note -->
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <h2>
            <i class="fa fa-fw fa-lg fa-home"></i>
            {{ $label("common.address", "Address") }}
          </h2>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Country -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.country", "Country")
                    }}</label>

                    <label class="select">
                      <select
                        v-model="selectedOrganization.address.country_id"
                        @change="onChangeSelectedCountry($event)"
                        id="organization-address-country"
                        :disabled="updating"
                      >
                        <option
                          v-for="country in countries"
                          :value="country.id"
                          :id="'organization-address-country-' + country.id"
                          :selected="countryShouldBeSelected(country)"
                          v-bind:key="country.id"
                          >{{ $label("common.country_" + country.slug) }}
                        </option>
                      </select>
                      <i></i>
                    </label>

                    <p
                      v-if="hasError('address.country_id')"
                      class="text-danger"
                    >
                      {{ getError("address.country_id") }}
                    </p>
                  </section>
                  <!-- END Country -->

                  <!-- BEGIN Country states -->
                  <section class="col col-6">
                    <label class="label">{{
                      $label("common.country_states", "States")
                    }}</label>

                    <label class="select">
                      <select
                        v-model="selectedOrganization.address.state_id"
                        :disabled="selectedCountry.states == null"
                        id="organization-address-state"
                      >
                        <option
                          v-for="state in selectedCountry.states"
                          :value="state.id"
                          :id="'organization-address-state-' + state.id"
                          :selected="stateShouldBeSelected(state)"
                          v-bind:key="state.id"
                          >{{
                            $label("common.state_" + state.slug, state.name)
                          }}
                        </option>
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('address.state_id')" class="text-danger">
                      {{ getError("address.state_id") }}
                    </p>
                  </section>
                  <!-- END Country states -->
                </div>

                <div class="row">
                  <!-- BEGIN Address city -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOrganization.address.city"
                      id="organization-address-city"
                      :label="$label('common.city', 'City')"
                      :placeholder="$label('common.city', 'City')"
                      :has-error="hasError('address.city')"
                      :error="getError('address.city')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Address city -->

                  <!-- BEGIN Post code -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.address.post_code"
                      id="organization-address-post-code"
                      :label="$label('common.post_code', 'Post code')"
                      :placeholder="$label('common.post_code', 'Post code')"
                      :has-error="hasError('address.post_code')"
                      :error="getError('address.post_code')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Post code -->
                </div>
                <div class="row">
                  <!-- BEGIN Street name -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOrganization.address.street_name"
                      id="organization-address-street-name"
                      :label="$label('common.street_name', 'Street')"
                      :placeholder="$label('common.street_name', 'Street')"
                      :has-error="hasError('address.street_name')"
                      :error="getError('address.street_name')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Street name -->

                  <!-- BEGIN Street number -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.address.street_number"
                      id="organization-address-street-number"
                      :label="$label('common.street_number', 'Street number')"
                      :placeholder="
                        $label('common.street_number', 'Street number')
                      "
                      :has-error="hasError('address.street_number')"
                      :error="getError('address.street_number')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Street number -->
                </div>

                <!-- BEGIN Address info -->
                <section>
                  <label class="label">{{
                    $label(
                      "common.address_additional_information",
                      "Additional information"
                    )
                  }}</label>
                  <label class="textarea">
                    <textarea
                      v-model="
                        selectedOrganization.address.additional_information
                      "
                      :class="{
                        'text-input-error': hasError(
                          'address.additional_information'
                        )
                      }"
                      :placeholder="
                        $label(
                          'common.address_additional_information',
                          'Additional information'
                        )
                      "
                      :disabled="updating"
                      id="organization-address-additional-information"
                      rows="3"
                    >
                    </textarea>
                  </label>
                </section>
                <!-- END Address info -->
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <h2>{{ $label("organization.contact", "Contact") }}</h2>
          <p></p>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <!-- BEGIN Phone prefix -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.phone_number.prefix"
                      id="organization-phone-prefix"
                      :label="
                        $label('organization.phone_prefix', 'Mobile prefix')
                      "
                      :placeholder="
                        $label('organization.phone_prefix_placeholder', 'XX')
                      "
                      :has-error="hasError('phone_number.prefix')"
                      :error="getError('phone_number.prefix')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Phone prefix -->

                  <!-- BEGIN Phone number -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOrganization.phone_number.number"
                      id="organization-phone-number"
                      :label="
                        $label('organization.phone_number', 'Mobile number')
                      "
                      :placeholder="
                        $label(
                          'organization.phone_number_placeholder',
                          'XXXXXXXXXX'
                        )
                      "
                      :has-error="hasError('phone_number.number')"
                      :error="getError('phone_number.number')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Phone number -->
                </div>
                <div class="row">
                  <!-- BEGIN Fax prefix -->
                  <section class="col col-4">
                    <text-input
                      v-model="selectedOrganization.fax_number.prefix"
                      id="organization-fax-prefix"
                      :label="$label('organization.fax_prefix', 'Fax prefix')"
                      :placeholder="
                        $label('organization.fax_prefix_placeholder', 'XX')
                      "
                      :has-error="hasError('fax_number.prefix')"
                      :error="getError('fax_number.prefix')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Fax prefix -->

                  <!-- BEGIN Fax number -->
                  <section class="col col-8">
                    <text-input
                      v-model="selectedOrganization.fax_number.number"
                      id="organization-fax-number"
                      :label="$label('organization.fax_number', 'Fax number')"
                      :placeholder="
                        $label(
                          'organization.fax_number_placeholder',
                          'XXXXXXXXXX'
                        )
                      "
                      :has-error="hasError('fax_number.number')"
                      :error="getError('fax_number.number')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Fax prefix -->
                </div>
                <div class="row">
                  <!-- BEGIN Website -->
                  <section class="col col-sm-12">
                    <text-input
                      v-model="selectedOrganization.website"
                      id="organization-website"
                      :label="$label('organization.website', 'Website')"
                      :placeholder="$label('organization.website', 'Website')"
                      :has-error="hasError('website')"
                      :error="getError('website')"
                      :is-disabled="updating"
                      @keyup.native.enter="createOrUpdateOrganization"
                    />
                  </section>
                  <!-- END Website -->
                </div>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="selectedOrganizationId != null && customersLength > 0"
        class="row"
      >
        <div class="col-sm-12">
          <br />
          <h2>{{ $label("organization.members", "Members") }}</h2>
          <p></p>
          <p></p>
          <div class="v-form-partial">
            <div class="v-form">
              <div class="table-responsive table-organization-customers">
                <table
                  class="table table-striped table-hover"
                  id="table-customers"
                >
                  <thead>
                    <tr>
                      <th>
                        {{ $label("common.name", "Name") }}
                      </th>
                      <th class="table-customers-type text-center">
                        {{ $label("common.type", "Type") }}
                      </th>
                      <th
                        class="text-center table-organization-customers-actions"
                      >
                        {{ $label("common.actions", "Actions") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="customer in customers" v-bind:key="customer.id">
                      <td class="v-table sorting_1">
                        {{
                          beautifyName(customer.first_name, customer.last_name)
                        }}
                      </td>
                      <td class="v-table" align="center">Type</td>
                      <td class="v-table" align="center">
                        <div class="btn-group">
                          <router-link
                            :to="{
                              name: 'CustomerData',
                              params: { customer_id: customer.id }
                            }"
                            v-tooltip="{
                              content: $label('common.details', 'Details'),
                              classes: 'custom-tooltip'
                            }"
                            data-id="customerId"
                            class="btn btn-default btn-default-sm"
                            ><i class="text-primary fa fa-lg fa-fw fa-eye"></i>
                            {{
                              $label("common.details", "Details")
                            }}</router-link
                          >
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div v-show="currentPage != 1 || !disabledNextPage" class="col-sm-12">
          <div
            class="dataTables_paginate paging_bootstrap_full_number"
            style="text-align: right; margin-right: 10px; margin-top:10px;"
          >
            <ul class="pagination mt-0" style="visibility: visible;">
              <li class="prev" :class="{ disabled: disabledFirstPage }">
                <a
                  @click="goToFirstPage"
                  v-tooltip="{
                    content: $label('common.first_page', 'First page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-left"></i
                ></a>
              </li>
              <li class="prev" :class="{ disabled: disabledPreviousPage }">
                <a
                  @click="goToPreviousPage"
                  v-tooltip="{
                    content: $label('common.back'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-left"></i
                ></a>
              </li>
              <li class="active">
                <a>{{ currentPage }}</a>
              </li>
              <li class="next" :class="{ disabled: disabledNextPage }">
                <a
                  @click="goToNextPage"
                  v-tooltip="{
                    content: $label('common.next', 'Next'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-right"></i
                ></a>
              </li>
              <li class="next" :class="{ disabled: disabledLastPage }">
                <a
                  @click="goToLastPage"
                  v-tooltip="{
                    content: $label('common.last_page', 'Last page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-right"></i
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        v-show="selectedOrganizationId != null && customersLength == 0"
        class="row mb-4"
      >
        <div class="col-xs-12"></div>
      </div>

      <div
        v-show="selectedOrganizationId != null && customersLength == 0"
        class="row"
      >
        <div class="col-sm-12">
          <div class="v-form-partial">
            <alert-box
              id="alert-no-organization-customers"
              type="warning"
              :closeable="false"
              :show-icon="true"
            >
              {{
                $label(
                  "organization.alert_no_customers",
                  "This organization has no customers."
                )
              }}
            </alert-box>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>
      <hr />

      <div class="row">
        <div class="col-xs-3 text-center">&nbsp;</div>

        <div class="col-xs-6 text-center">
          <back-button
            back-link="OrganizationsOverview"
            :class="{ 'is-disabled': updating }"
            id="back-organization-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-organization-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateOrganization"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedOrganizationId == null
            }"
            id="delete-organization-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteOrganization"
          >
          </danger-button>
        </div>

        <div class="col-xs-3 text-center">&nbsp;</div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import swal from "sweetalert";
import Api from "@/api";

import FormErrorsMixin from "@/mixins/FormErrors";
import ViewLoader from "@/components/Common/ViewLoader";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import TextInput from "@/components/Common/Inputs/TextInput";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import OrganizationActions from "@/components/Settings/Organizations/OrganizationActions";
import Error404 from "@/components/Common/Error404";

export default {
  name: "OrganizationDetails",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    AlertBox,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    OrganizationActions,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      countries: [],
      selectedCountry: {
        states: null
      },
      originalName: null,
      selectedOrganization: {
        name: null,
        type: null,
        email: null,
        founding_year: null,
        internal_note: null,
        website: null,
        uid: null,
        dvr: null,
        zvr: null,
        phone_number: {
          prefix: null,
          number: null
        },
        fax_number: {
          prefix: null,
          number: null
        },
        address: {
          country_id: null,
          state_id: null,
          city: null,
          post_code: null,
          street_name: null,
          street_number: null,
          additional_information: null
        }
      },
      selectedOrganizationId: null,
      organizationTypes: [],
      failedRequest: false,
      validatedInputs: [
        "name",
        "type",
        "email",
        "founding_year",
        "internal_note",
        "website",
        "uid",
        "dvr",
        "zvr",
        "phone_number.prefix",
        "phone_number.number",
        "fax_number.prefix",
        "fax_number.number",
        "address.country_id",
        "address.state_id",
        "address.city",
        "address.post_code",
        "address.street_name",
        "address.street_number",
        "address.additional_information"
      ],
      // customers data
      customers: [],
      customersLength: null,
      currentPage: 1,
      total: 0,
      from: 0,
      to: 0,
      firstPage: 1,
      lastPage: null,
      perPage: null,
      searchFilter: ""
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getOrganizationTypes();
      vm.getCountries();
      vm.selectedOrganizationId = vm.$route.params.organization_id;
      if (
        typeof vm.selectedOrganizationId !== "undefined" &&
        vm.selectedOrganizationId !== null
      ) {
        if (vm.$route.query.organization_created) {
          swal("Success!", "The organization was created.", "success").then(
            () => {
              window.history.pushState(
                {},
                document.title,
                window.location.pathname
              );
            }
          );
        }
        vm.getOrganization(() => {
          //
        });
        vm.getOrganizationCustomers();
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getOrganizationTypes();
    this.getCountries();
    if (
      typeof this.selectedOrganizationId !== "undefined" &&
      this.selectedOrganizationId !== null
    ) {
      this.getOrganization(() => {
        //
      });
      this.getOrganizationCustomers();
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateOrganization() {
      if (this.selectedOrganizationId == null) {
        this.createOrganization();
      } else {
        this.updateOrganization();
      }
    },

    getOrganizationTypes() {
      const endpoint = this.$partnerEndpoint("organizations/types");

      Api()
        .get(endpoint)
        .then(response => {
          this.organizationTypes = response.data;
        })
        .catch(error => {
          console.log(error.response);
        });
    },

    createOrganization() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("organizations");

      Api()
        .post(endpoint, this.selectedOrganization)
        .then(response => {
          this.selectedOrganizationId = response.data.data.id;
          this.$router.push({
            name: "OrganizationDetails",
            params: { organization_id: this.selectedOrganizationId },
            query: { organization_created: true }
          });
          this.$scrollTo("#organization-name", 500);
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#organization-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getOrganization() {
      if (this.loading) {
        return;
      }

      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "organizations/" + this.selectedOrganizationId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedOrganization = response.data.data;
          this.originalName = this.selectedOrganization.name;
          this.setSelectedCountry(this.selectedOrganization.address.country_id);
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateOrganization() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "organizations/" + this.selectedOrganizationId
      );

      Api()
        .put(endpoint, this.selectedOrganization)
        .then(() => {
          this.getOrganization(() => {
            //
          });
          swal({
            title: "Success!",
            text: "The organization was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#organization-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteOrganization() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The organization will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "organizations/" + this.selectedOrganizationId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "OrganizationsOverview",
                query: { organization_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete organizations.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getOrganizationCustomers(successCallback) {
      this.customers = [];
      const endpoint = this.$partnerEndpoint(
        "organizations/" + this.selectedOrganizationId + "/customers"
      );

      Api()
        .get(endpoint, {
          params: { page: this.currentPage, search: this.searchFilter }
        })
        .then(response => {
          this.customers = response.data.data;
          this.currentPage = response.data.meta.current_page;
          this.total = response.data.meta.total;
          this.from = response.data.meta.from;
          this.to = response.data.meta.to;
          this.lastPage = response.data.meta.last_page;
          this.perPage = response.data.meta.per_page;
          this.customersLength = this.customers.length;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    organizationTypeShouldBeSelected(organizationType) {
      return this.selectedOrganization.type == organizationType;
    },

    getCountries(successCallback) {
      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    countryShouldBeSelected(country) {
      return this.selectedOrganization.address.country_id == country.id;
    },

    stateShouldBeSelected(state) {
      return this.selectedOrganization.address.state_id == state.id;
    },

    setSelectedCountry(countryId) {
      for (let i = 0; i < this.countries.length; i++) {
        if (countryId == this.countries[i].id) {
          this.selectedCountry = this.countries[i];
        }
      }
    },

    onChangeSelectedCountry(event) {
      for (let i = 0; i < this.countries.length; i++) {
        if (event.target.value == this.countries[i].id) {
          this.selectedCountry = this.countries[i];
        }
      }
    },

    goToFirstPage() {
      if (!this.disabledFirstPage) {
        this.currentPage = 1;
        this.getOrganizationCustomers(() => {
          //
        });
      }
    },

    goToPreviousPage() {
      if (!this.disabledPreviousPage) {
        this.currentPage--;
        this.getOrganizationCustomers(() => {
          //
        });
      }
    },

    goToNextPage() {
      if (!this.disabledNextPage) {
        this.currentPage++;
        this.getOrganizationCustomers(() => {
          //
        });
      }
    },

    goToLastPage() {
      if (!this.disabledLastPage) {
        this.currentPage = this.lastPage;
        this.getOrganizationCustomers(() => {
          //
        });
      }
    }
  },

  computed: {
    disabledFirstPage() {
      return this.currentPage === 1;
    },

    disabledPreviousPage() {
      return this.currentPage === 1;
    },

    disabledNextPage() {
      return this.currentPage === this.lastPage;
    },

    disabledLastPage() {
      return this.currentPage === this.lastPage;
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-0 {
  margin-top: 0rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0rem;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}
</style>
